import { CheckCircle2, Rocket, Users, Layers, Lightbulb, ShieldCheck, ArrowRight, Hand, BookOpen, Download } from "lucide-react";
import { Link } from "react-router-dom";
import { useBrochure } from "@/components/BrochureGate";

const CURRICULUM_KEYS = ["llm", "prompt", "agentic"];
import { useState } from "react";
import { InterestDialog } from "./InterestDialog";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { PATHWAY, PATHWAY_BADGES } from "@/data/offers";
import { VISUALS } from "./PathwayVisuals";
import { useDiagnostic } from "./DiagnosticContext";
import { Reveal } from "./Reveal";

const TONES = {
  teal: { card: "from-teal-50 to-white border-teal-100", num: "bg-[#0E9384]", check: "text-[#0E9384]", mini: "text-teal-700", chip: "bg-teal-50 text-teal-800" },
  blue: { card: "from-blue-50 to-white border-blue-100", num: "bg-blue-600", check: "text-blue-600", mini: "text-blue-700", chip: "bg-blue-50 text-blue-800" },
  sky: { card: "from-sky-50 to-white border-sky-100", num: "bg-sky-600", check: "text-sky-600", mini: "text-sky-700", chip: "bg-sky-50 text-sky-800" },
  navy: { card: "from-slate-100 to-white border-slate-200", num: "bg-[#0B1B33]", check: "text-[#0B1B33]", mini: "text-[#0B1B33]", chip: "bg-slate-100 text-[#0B1B33]" },
};
const BADGE_ICONS = { users: Users, layers: Layers, bulb: Lightbulb, shield: ShieldCheck };

const Course = ({ c, i, onInterest }) => {
  const { request } = useBrochure();
  const t = TONES[c.tone];
  const V = VISUALS[c.visual];
  return (
    <Reveal delay={i * 0.08} className="h-full">
      <article data-testid={`pathway-course-${c.key}`} className={`flex h-full flex-col rounded-3xl border bg-gradient-to-b p-5 ${t.card}`}>
        <div className="flex items-start gap-4">
          <span className={`font-display grid h-12 w-12 shrink-0 place-items-center rounded-full text-[22px] font-bold text-white shadow-lg ${t.num}`}>{c.n}</span>
          <div className="min-w-0">
            <h3 className="font-display text-[20px] font-bold leading-tight text-[#0B1B33]">{c.title}</h3>
            <p className="mt-1 text-[14px] leading-snug text-slate-700">{c.sub}</p>
          </div>
        </div>
        <div className="mt-4 flex flex-wrap items-center gap-2">
          <span data-testid={`pathway-fee-${c.key}`} className="rounded-full bg-[#0B1B33] px-3 py-1 text-[13px] font-semibold text-white">{c.fee.startsWith("₹") ? `Course fee ${c.fee}` : c.fee}</span>
          {c.meta && <span className="rounded-full bg-white px-3 py-1 text-[12px] font-medium text-slate-700 shadow-sm">{c.meta}</span>}
        </div>
        <div className="mt-4"><V /></div>
        <button data-testid={`interest-btn-${c.key}`} onClick={() => onInterest(c)}
          className="btn-teal mt-4 flex w-full items-center justify-center gap-2 rounded-xl py-3 text-[15px] font-semibold">
          <Hand className="h-4 w-4" /> I'm interested <ArrowRight className="arrow h-4 w-4" />
        </button>
        {CURRICULUM_KEYS.includes(c.key) && (
          <div className="mt-2 grid grid-cols-2 gap-2">
            <Link data-testid={`curriculum-view-${c.key}`} to={`/courses/${c.key}`} className="flex items-center justify-center gap-1.5 rounded-xl border border-slate-300 bg-white py-2.5 text-[13.5px] font-semibold text-[#0B1B33] transition-colors hover:border-[#0E9384]">
              <BookOpen className="h-4 w-4" /> View curriculum
            </Link>
            <button data-testid={`curriculum-pdf-${c.key}`} onClick={() => request({ key: c.key, title: c.title })} className="flex items-center justify-center gap-1.5 rounded-xl border border-slate-300 bg-white py-2.5 text-[13.5px] font-semibold text-[#0B1B33] transition-colors hover:border-[#0E9384]">
              <Download className="h-4 w-4" /> Download PDF
            </button>
          </div>
        )}
        <ul className="mt-4 space-y-2">
          {c.bullets.map((b) => <li key={b} className="flex gap-2 text-[14px] leading-snug text-slate-800"><CheckCircle2 className={`mt-0.5 h-4 w-4 shrink-0 ${t.check}`} />{b}</li>)}
        </ul>
        <div className="mt-4 flex gap-3 rounded-xl border border-white bg-white/90 p-3 shadow-sm">
          <Rocket className={`h-8 w-8 shrink-0 ${t.check}`} strokeWidth={1.6} />
          <div><p className={`font-display text-[15px] font-bold ${t.mini}`}>Mini Project:</p><p className="text-[14px] font-semibold text-[#0B1B33]">{c.mini.title}</p><p className="text-[13px] text-slate-600">{c.mini.text}</p></div>
        </div>
        <p className="font-display mt-4 text-[15px] font-bold text-[#0B1B33]">Who this is for?</p>
        <div className="mt-2 flex flex-wrap gap-1.5">{c.who.map((w) => <span key={w} className={`rounded-lg px-2.5 py-1 text-[12px] font-medium ${t.chip}`}>{w}</span>)}</div>
        <p className="font-display mt-5 text-[15px] font-bold text-[#0B1B33]">More Details <span className="font-normal text-slate-600">(click to expand)</span></p>
        <Accordion type="single" collapsible className="mt-1">
          {c.details.map((d, j) => (
            <AccordionItem key={d.t} value={d.t} className="border-slate-200/80">
              <AccordionTrigger data-testid={`pathway-${c.key}-detail-${j}`} className="py-2.5 text-[14px] font-medium text-slate-800 hover:no-underline">{d.t}</AccordionTrigger>
              <AccordionContent><ul className="list-disc space-y-1 pl-5 text-[13.5px] text-slate-700">{d.items.map((x) => <li key={x}>{x}</li>)}</ul></AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </article>
    </Reveal>
  );
};

export const Pathway = () => {
  const { open } = useDiagnostic();
  const [interest, setInterest] = useState(null);
  return (
    <section id="pathway" data-testid="pathway-section" className="mx-auto max-w-[1440px] px-5 pb-16 lg:px-10">
      <Reveal className="text-center">
        <h2 className="font-display text-[34px] font-extrabold leading-[1.05] tracking-tight text-[#0B1B33] md:text-[48px]">
          Your <span className="bg-gradient-to-r from-[#0E9384] to-[#2563EB] bg-clip-text text-transparent">4-Step</span> Learning Pathway
        </h2>
        <p className="mx-auto mt-3 max-w-2xl text-base text-slate-700 md:text-lg">Learn to work forward from the problem, not backward from a framework name. Each course has one clear job; choose the stage that fits you.</p>
      </Reveal>
      <div className="mt-8 grid grid-cols-2 gap-3 lg:grid-cols-4">
        {PATHWAY_BADGES.map((b, i) => {
          const I = BADGE_ICONS[b.icon];
          return (
            <Reveal key={b.title} delay={i * 0.06}>
              <div className="flex h-full items-center gap-3 rounded-2xl border border-slate-200 bg-white p-3.5">
                <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-teal-50 text-[#0E9384]"><I className="h-5 w-5" /></span>
                <div><p className="text-[13.5px] font-semibold leading-tight text-[#0B1B33]">{b.title}</p><p className="text-[12.5px] text-slate-600">{b.text}</p></div>
              </div>
            </Reveal>
          );
        })}
      </div>
      <div className="mt-6 grid items-start gap-5 md:grid-cols-2 xl:grid-cols-4">
        {PATHWAY.map((c, i) => <Course key={c.key} c={c} i={i} onInterest={setInterest} />)}
      </div>
      <Reveal className="mt-8 flex flex-col items-center gap-3 text-center">
        <p className="max-w-2xl text-[14px] text-slate-600">This is a learning pathway, not a compulsory package. Tool, API and cloud charges, dates, format and refund terms are confirmed before any payment.</p>
        <button data-testid="pathway-diagnostic-btn" onClick={() => open()} className="btn-teal flex items-center gap-2 rounded-xl px-7 py-4 text-[16px] font-semibold">
          Find my right starting course <ArrowRight className="arrow h-5 w-5" />
        </button>
      </Reveal>
      <InterestDialog course={interest} onClose={() => setInterest(null)} />
    </section>
  );
};
